import { computed, action, observable } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type Collaborator, type RatingMethod } from '@/features/jobs/Job'

/**
 * View Model for rating a pro.
 */
export class RateProDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable
  accessor feedback = ''

  @observable
  accessor thumbsRating: boolean | undefined = undefined

  @observable
  accessor rating = 0

  constructor(
    private readonly _getProChampion: () => Collaborator | undefined,
    private readonly _getRatingMethod: () => RatingMethod,
    private readonly _submitProRating: (
      rating: number,
      feedback: string,
    ) => Promise<void>,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  @computed
  get proChampion() {
    return this._getProChampion()
  }

  @computed
  get ratingMethod() {
    return this._getRatingMethod()
  }

  @computed
  get validRating() {
    return this.rating >= 1 && this.rating <= 5
  }

  @action.bound
  setRating(value: number) {
    this.rating = value
  }

  @action.bound
  setThumbsRating(value: boolean) {
    this.thumbsRating = value
    this.rating = this.thumbsRating ? 4 : 2
  }

  ratePro = task.resolved(async () => {
    if (!this.validRating) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Submitting rating')
      .autoDismiss()
      .show()

    try {
      await this._submitProRating(this.rating, this.feedback)

      alert
        .withState('success')
        .saying('Success!', 'Your rating was submitted')
        .autoDismiss()

      this.modal.close()
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Submitting the rating failed')(err)
    }
  })
}
